import { EMPLOYER, JOB_TITLE } from "./profile";

export const SessionType = {
  Talk: "Talk",
  LightningTalk: "Lightning talk",
  Workshop: "Workshop",
  Mentoring: "Mentoring",
  PanelDiscussion: "Panel discussion",
} as const;
export type SessionType = (typeof SessionType)[keyof typeof SessionType];

export const LANGUAGES = { en: "English", de: "German" } as const;
export type LanguageCode = keyof typeof LANGUAGES;

export type DurationMinutes = number | [number, number];

export type Session = {
  title: string;
  type: SessionType;
  description?: string | null;
  languageCode?: LanguageCode | null;
  slotLabel?: string | null;
  recordingUrl?: string | null;
  slidesUrl?: string | null;
  codeUrl?: string | null;
  durationMinutes?: DurationMinutes | null;
};

export type LocationDefinition = {
  city: string;
  country: string;
  coordinates?: [number, number];
};

export type EventItem = {
  name: string;
  startDate: string;
  endDate?: string | null;
  location?: LocationDefinition | null;
  website?: string | null;
  talks: Session[];
};

export const DATA = {
  firstName: "François",
  lastName: "Martin",
  tagline: `${JOB_TITLE} at ${EMPLOYER}`,
  bio: `François Martin is a senior full-stack software engineer at Karakun AG in Switzerland, passionate about building high-quality applications. He co-authored the open-source JavaFX frameworks WorkbenchFX and PreferencesFX, and organizes Basel's Hackergarten to help developers get involved with open-source projects.

François is an Oracle ACE Associate and co-authored the ISTQB Test Analyst v4.0 syllabus. He is also part of the content committees for the BaselOne and DevBcn conferences.

François has given 85 talks, workshops, and mentoring sessions across 18 countries. He has spoken at major international conferences, including WeAreDevelopers World Congress, JavaZone, NDC Oslo, Jfokus, KCDC, Devnexus, and Devoxx Greece. He regularly shares practical insights to inspire developers and help teams build better software.`,
  photoLargeUrl: "/francois.jpg",
};

export const SESSIONS: Record<string, Session> = {
  javaStreams: {
    title: "I Didn't Know You Could Do That With Java Streams!?",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [30, 90],
    description: `Over the years of working with many Java codebases, I have seen the same mistakes made over and over again when using Streams. Most Java developers are familiar with the basics, but many are unaware of advanced features that can make their code easier to read and perform better at the same time. In this talk, I will share the common problems I faced and how I used advanced Stream techniques and Collectors to improve those codebases.

You will learn how to avoid common mistakes that slow applications down and how you can unlock the full potential that Streams have to offer. I will demonstrate powerful yet straightforward ways to write cleaner, more efficient code, based on examples from my real-world experience and the new Stream Gatherers introduced in Java 24. You will see how to transform the way you write Java code, improving both performance and readability.

This talk is aimed at Java developers who have previously used Streams but want to expand their knowledge with more advanced concepts. Join me as I share what I have learned from improving multiple codebases and see how making the right changes can make your Java code better and easier to maintain.`,
  },
  testingWaste: {
    title: "Why Software Testing Is a Waste of Time",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [30, 45],
    description: `Testing software takes up a lot of time in the software development process, often as much time as, if not more than, the time required for development itself. Just as development does, testing also requires ongoing maintenance. This (rightfully so) makes a lot of people in different roles, be it managers, developers, or even testers, question whether or not that time and effort is really justified. With more and more companies deploying every 10 minutes or even less, people start to wonder: why invest time in testing when you can rollback issues in a matter of seconds, in some cases even automatically?

Learn more about common pitfalls, time wasters, and what you can do to make your testing strategy more efficient in this talk!`,
  },
  vulnDisclosure: {
    title: "The Art of Vulnerability Disclosure",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [45, 60],
    description: `Properly disclosing a vulnerability you find in a website, open-source project, or app is not easy, especially the first time. There are many things to pay attention to, and that can go wrong. But if done properly, it can be very rewarding, as most companies are very grateful. Whether you want to learn how to disclose your first vulnerability in the easiest way possible or you want to hear about best practices and suggestions on what to do when things do not go as planned, this talk is for you.`,
  },
  flakyTests: {
    title: "Testing on Thin Ice: Chipping Away at Test Unpredictability",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [45, 60],
    description: `Ever tried to catch melting snowflakes? That's the challenge of dealing with flaky tests - those annoying, unpredictable tests that fail randomly and pass when rerun. In this talk, we'll slide down the slippery slope of why flaky tests are more than just a nuisance. They're time-sinks, morale crushers, and silent code quality killers.

We'll skate across real-life scenarios to understand how flaky tests can freeze your development in its tracks, and why sweeping them under the rug is like ignoring a crack in the ice. From delayed releases to lurking bugs, the stakes are high, and the costs are real.

But don't pack your parkas just yet! We're here to share expert strategies and insights on how to identify, analyze, and ultimately melt away these flaky tests. Through our combined experience, we'll provide actionable techniques and tools to ensure snow is the only flakiness you experience, ensuring a smoother and more reliable journey in software development.`,
  },
  apollo: {
    title:
      "One Small Test for Man, One Giant Leap for Software Quality: Adopting Apollo's Testing Principles",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [45, 60],
    description: `The Apollo missions did more than take humanity to the moon - they highlighted the essential role of testing in achieving ambitious goals. NASA's report, 'What Made Apollo a Success,' offers a detailed look at the testing strategies crucial to propelling the missions to success. This talk focuses on actionable insights from that report that we can still use today. Find out how you can adopt the approach that landed astronauts on the moon to elevate software quality, making every test a launchpad for unparalleled reliability and performance.`,
  },
  jmh: {
    title: "My code is faster than yours... let me prove it to you!",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [45, 60],
    description: `You stare at the notification on your screen, your heart sinking with each passing second. It is a pull request from your colleague, Lisa, asking you to review her code. You take a deep breath and click on the link, hoping for a smooth review process. Little did you know, this simple task would soon turn into a daunting challenge.

As you scan through the code, one particular line catches your attention - a regular expression. Immediately, you knew this was a problem waiting to happen. You leave a comment on the pull request, expressing your concerns about the regex. You point out that it is not only slow but also difficult to read and maintain. You suggest Lisa replace it with a more explicit block of code that achieves the same result, albeit at the cost of increased length.

Within minutes, Lisa responded to your comment, refusing to make any changes. She argues her regex is not only shorter but also just as easy to understand. She challenges you, stating she wouldn't change it unless you could prove your proposed solution was faster.

Your panic levels begin to rise. You have never done any performance comparisons before. You desperately search through your bookmarks, hoping to find something that could help you in this predicament. Then it strikes you - you remember that François Martin will give a conference talk showing how to do microbenchmarking in Java using JMH, scheduled to take place in just a few days. It was like a lifeline was thrown to you. With renewed hope, you quickly register for the conference, eager to gain the knowledge you need to save this code review. You sigh with relief, marking the talk's date in your calendar with a red pen and counting down the days until the conference...`,
    codeUrl: "https://github.com/martinfrancois/microbenchmark-java",
  },
  browserSnitch: {
    title: "How your browser is snitching on you and how you can take control",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [30, 45],
    description: `With data now being "the new digital currency", knowing what data we share is more important than it was ever before.

The browsers we use every day reveal much more information than we would think when visiting websites. This makes it easy for websites to uniquely identify us as visitors, even without a login. Trying to protect ourselves against this could even make it easier for websites to identify us, which is a big dilemma.

This talk is about what the different browsers reveal to the websites we visit and what we can (or cannot) do about it.`,
  },
  treeShaking: {
    title: "How writing just one import the wrong way slows down your website",
    type: SessionType.LightningTalk,
    languageCode: "en",
    description: `Tree shaking is a feature that is used by modern bundlers to keep bundle size down (and load times fast), but if just one import is written in a non-optimal way, this can result in the whole dependency being included in the bundle. In some cases, this even happens with automatically suggested imports by the IDE. Learn about the correct way of importing from dependencies, tricks you can use to easily determine the impact on bundle size, and why having a lot of dependencies may not be as bad as you think!`,
  },
  testingToolbox: {
    title: "From Bugs to Brilliance: Testing Toolbox in Action",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [30, 90],
    description: `What if I told you that there is an entire world of lesser-known, underutilized testing tools, libraries, and features waiting to elevate your testing game? You will see live demonstrations of a plethora of these hidden gems, carefully curated to empower you in unit, integration, and end-to-end testing. This includes both tools for Java backends and JavaScript/TypeScript frontends as well as some technology-independent tools.

As you absorb the knowledge and sharpen your skills, you will accumulate an impressive set of tools in your testing toolbox. However, a word of caution: your toolbox may grow heavy with all these new additions. You will leave this talk armed with newfound knowledge, but make sure you can still lift your toolbox by the end of it!`,
    codeUrl: "https://github.com/martinfrancois/testing-toolbox",
  },
  bugs82: {
    title:
      "82 Bugs I Collected in a Year You Won't Believe Made It to Production",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [45, 60],
    description: `This past year, I made it my mission to collect every bug I encountered while using everyday apps and websites. From minor glitches to major problems, I ended up with a list of 82 bugs that somehow made it to production. These real-world examples are more than just interesting - they're also embarrassing mistakes that teach important lessons for making software more reliable and improving how we test it.

In this talk, I will show the different types of bugs I found and what they reveal about common mistakes in software development. I will also highlight areas that are often overlooked but are crucial to the quality of your product.

By looking at these bugs together, I'll show you how to avoid them, so these issues don't reach your users. Whether you want to make your code more reliable or reduce the time from ticket creation to fix, I'll share actionable advice you can apply the next day. Join me to learn from the embarrassing mistakes that even popular apps couldn't avoid - and take-home strategies to prevent them in your software!`,
  },
  teachingAiBetterCodingHabits: {
    title: "My Fixes Became the Lesson: Teaching AI Better Coding Habits",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: 50,
    description: `The worst code is not the code that is clearly wrong. It is the code that is almost right.

<strong>Coding agents are painfully good at almost right.</strong>

They can write code that compiles, looks modern, and seems fine at first glance. Then you look closer: one line uses an anti-pattern. You ask the agent to clean it up, and it replaces that anti-pattern with another one. So you try again, more carefully, with the kind of prompt that should leave no room for misunderstanding.

<strong>It avoids the pattern you called out and finds a third way to be almost right.</strong>

I know. I did not believe it either.

At that point, I was so frustrated that all I could think was, "I need to make sure I never have to go through this again."

I needed the agent to get better. More skilled, really.<br />Wait.<br /><strong>Skill.</strong><br />That is what I needed.

So I made the failure repeatable, wrote down the lesson behind the fix, and tested whether the agent stopped falling for it.

I will walk through that path with real generated code: the bad output, the better version, the skill, and the tests that proved it worked. The examples use Java streams and optionals, but the method works for any language, library, or coding habit your coding agent keeps getting wrong.

You will leave knowing how to turn frustration into a skill: capture the almost-right output, write the version you wanted in the first place, turn the difference into a rule, test the same task with and without the skill, and prove you can turn almost-right into right - and keep it that way.`,
  },
  workshopContributingOSS: {
    title: "From Zero to Maintainer: Contributing to Open Source Projects",
    type: SessionType.Workshop,
    languageCode: "en",
    description: `This full-day workshop demystifies open-source contribution by guiding participants through every step, from discovering projects and filing their first issues to drafting and submitting pull requests in a safe sandbox. You'll practice the OSS workflow hands-on, including forking, branching, implementing changes, and crafting clear pull requests (PRs). Along the way, you'll also step into the shoes of a maintainer, simulating triage, release tagging, and community-management tasks, so you leave not only confident in contributing code but ready to steward and grow healthy open-source projects.

In this hands-on session, participants will:
<ul>
<li>Explore and evaluate open-source projects, learning how to read CONTRIBUTING guides and identify beginner-friendly issues.</li>
<li>Practice the core workflow: forking a repository, creating feature branches, making code or documentation changes, and submitting a pull request.</li>
<li>Configure basic CI checks (linting, tests) to ensure quality before merging.</li>
<li>Simulate maintainer duties, including triaging issues, applying semantic-version tags, and drafting release notes.</li>
<li>Discuss community best practices, including writing clear commit messages, utilizing issue labels effectively, and promoting positive contributor interactions.</li>
</ul>
By the end, everyone will have executed each stage of the contribution lifecycle in a controlled "sandbox" repo and gained a clear path for moving on to real-world projects.`,
  },
  workshopFlakyTestTrust: {
    title:
      "Stop Wasting Hours on Test Unpredictability and Get Back to Builds You Can Trust",
    type: SessionType.Workshop,
    languageCode: "en",
    durationMinutes: 480,
    description: `Your test fails. You rerun it. It passes. You rerun it again. It fails somewhere else.

That is not bad luck. That is evidence.

In this full-day hands-on workshop, you will work through real flaky test examples and turn random-looking failures into failures you can reproduce, understand, fix, and prevent. The examples use JavaScript/TypeScript and Playwright, but the learnings apply to other programming languages and testing frameworks as well.

You will find flaky tests with repeated runs, changed execution order, parallel execution, evidence from continuous integration, logs, screenshots, traces, and small experiments. Then you will trigger failures due to timing issues, shared test data, test-order dependencies, resource conflicts, differences in continuous integration, and external systems.

You will inspect the evidence, decide whether the failure is flaky or real, and fix the cause yourself.

You will also use agentic artificial intelligence tools to speed up the hardest parts of flaky-test work: reproducing intermittent failures, running targeted experiments, comparing results across repeated runs, and improving continuous integration checks to catch new flaky tests earlier. You will practice deciding when the tool is helping, when it is guessing, and when a suggested fix only hides the problem.

Join me to stop losing afternoons to tests that fail but pass when you rerun them. You will leave with a practical way to catch flaky tests early, reproduce them reliably, fix the root cause, and trust your pipeline again.`,
  },
  browser2025: {
    title: "Was dein Browser über dich verrät - 2025 Edition",
    type: SessionType.Talk,
    languageCode: "de",
    durationMinutes: [30, 45],
    description: `Unsere Browser, die wir täglich verwenden, geben beim Besuchen von Webseiten viel mehr Informationen preis, als wir denken. Damit wird es Webseiten leicht gemacht, uns als Besucher eindeutig zu identifizieren und das auch ohne Login. Versuchen wir uns zu schützen, könnte das dazu führen, dass wir es dadurch den Webseiten sogar noch leichter machen.

In diesem Vortrag geht es darum aufzuzeigen, was die verschiedenen Browser den Webseiten, die wir besuchen verraten und was wir dagegen tun können (oder auch nicht).`,
  },
  browser2024: {
    title: "Was dein Browser über dich verrät - 2024 Edition",
    type: SessionType.Talk,
    languageCode: "de",
    durationMinutes: [30, 45],
    description: `Unsere Browser, die wir täglich verwenden, geben beim Besuchen von Webseiten viel mehr Informationen preis, als wir denken. Damit wird es Webseiten leicht gemacht, uns als Besucher eindeutig zu identifizieren und das auch ohne Login. Versuchen wir uns zu schützen, könnte das dazu führen, dass wir es dadurch den Webseiten sogar noch leichter machen.

In diesem Vortrag geht es darum aufzuzeigen, was die verschiedenen Browser den Webseiten, die wir besuchen verraten und was wir dagegen tun können (oder auch nicht).`,
  },
  browser2023: {
    title: "Was dein Browser über dich verrät - 2023 Edition",
    type: SessionType.Talk,
    languageCode: "de",
    durationMinutes: [30, 45],
    description: `Unsere Browser, die wir täglich verwenden, geben beim Besuchen von Webseiten viel mehr Informationen preis, als wir denken. Damit wird es Webseiten leicht gemacht, uns als Besucher eindeutig zu identifizieren und das auch ohne Login. Versuchen wir uns zu schützen, könnte das dazu führen, dass wir es dadurch den Webseiten sogar noch leichter machen.

In diesem Vortrag geht es darum aufzuzeigen, was die verschiedenen Browser den Webseiten, die wir besuchen verraten und was wir dagegen tun können (oder auch nicht).`,
  },
  browser: {
    title: "Was dein Browser über dich verrät",
    type: SessionType.Talk,
    languageCode: "de",
    durationMinutes: [30, 45],
    description: `Unsere Browser, die wir täglich verwenden, geben beim Besuchen von Webseiten viel mehr Informationen preis, als wir denken. Damit wird es Webseiten leicht gemacht, uns als Besucher eindeutig zu identifizieren und das auch ohne Login. Versuchen wir uns zu schützen, könnte das dazu führen, dass wir es dadurch den Webseiten sogar noch leichter machen.

In diesem Vortrag geht es darum aufzuzeigen, was die verschiedenen Browser den Webseiten, die wir besuchen verraten und was wir dagegen tun können (oder auch nicht).`,
  },
  mentoringConferences: {
    title: "Using Conferences to Reach the Next Level",
    type: SessionType.Mentoring,
    languageCode: "en",
    description: `Conferences can be more than just learning opportunities - they can be a jumping-off point for career growth. Whether you want to become a recognized expert or speaker, transition into developer advocacy, or expand your professional network, this mentoring session will help you leverage conferences to achieve your goals.

Who it is for: Developers, software engineers, and (aspiring) speakers looking to grow their careers through conferences.

Takeaways:
<ul>
<li>Understand how speaking, networking, and strategic participation in conferences can open new career opportunities.</li>
<li>Learn practical steps to get accepted as a speaker, build meaningful connections, and make an impact.</li>
<li>Discover how to turn conference experiences into long-term career benefits, whether in developer advocacy, consulting, or leadership roles.</li>
</ul>`,
  },
  mentoringNextSteps: {
    title: "Next Steps for a Developer",
    type: SessionType.Mentoring,
    languageCode: "en",
    description: `Have you ever wondered what's next in your career? Whether you're considering consulting, DevOps, contributing to open source, or leadership, this mentoring session will help you explore the options available and map out a path that aligns with your skills and interests.

Who it is for: Mid-to-senior-level developers looking for new challenges or career growth opportunities.

Takeaways:
<ul>
<li>Discover different career paths beyond traditional development roles, from consulting to DevOps to open source and leadership.</li>
<li>Identify the key skills needed for these transitions and how to start developing them.</li>
<li>Learn actionable strategies to position yourself for your next career move while continuing to grow technically.</li>
</ul>`,
  },
  askMeAnything: {
    title: "Ask Me Anything",
    type: SessionType.Mentoring,
    languageCode: "en",
    durationMinutes: 60,
    description: `An interactive small-group Ask Me Anything session where conference attendees can ask speakers about their talks, industry trends, career advice, and practical experience.`,
  },
  publicSpeakingMentoring: {
    title: "Public Speaking",
    type: SessionType.Mentoring,
    languageCode: "en",
    durationMinutes: 60,
    description: `A small-group public-speaking mentoring session for developers who want feedback on abstracts, technical talk structure, stage presence, and conference speaking confidence.`,
  },
  aiAssistedCodingPanel: {
    title:
      "AI-Assisted Coding Beyond the Vibe: Craft, Economics, and the Human at the Controls",
    type: SessionType.PanelDiscussion,
    languageCode: "en",
    description: `A GeeCON discussion panel on AI-assisted coding beyond vibe coding, including software craft, economics, and the role of the human at the controls.`,
  },
  integrationTestingAndDevelopment: {
    title: "The Integration of Testing and Development",
    type: SessionType.PanelDiscussion,
    languageCode: "en",
    description: `In this panel discussion, I will draw from my extensive experience in both testing and development and share practical insights and strategies on how to achieve closer collaboration between developers and testers.

Discover how integrating testing early in the development process can enhance team satisfaction and lead to superior project outcomes. We will discuss real-world examples of successful integration, such as testers participating in pair programming sessions with developers and developers engaging in exploratory testing.

Join me to share your experiences, challenges and solutions, or to ask questions about anything you want to know. Whether you are a developer, a tester, or someone who wears both hats, this panel discussion will provide valuable perspectives and actionable ideas to improve your team's workflow and deliver better results.`,
  },
  panelAutomation: {
    title: "Mastering Test Automation: Strategies and Innovations",
    type: SessionType.PanelDiscussion,
    languageCode: "en",
    description: `This panel discussion will explore the latest trends, strategies, and innovative approaches in the field of test automation. Expert panelists will share insights on how to effectively implement and optimize test automation frameworks, address common challenges, and leverage cutting-edge tools and technologies.`,
  },
  workshopGitlabCI: {
    title: "From Zero to Continuous Delivery with GitLab CI Pipelines",
    type: SessionType.Workshop,
    languageCode: "en",
    description: `This interactive workshop is designed to equip you with the skills to set up and automate a GitLab CI pipeline from scratch. Suitable for both beginners and those with some experience, this session will focus on implementing continuous deployment for applications combining backend technologies (Java or Python) and frontend frameworks (React or Angular with TypeScript). Key topics include branch management, job dependencies, artifact handling, parallel execution, and environment variables. You will also learn to enforce best practices in software development such as static code analysis, secret scanning, and automated version control. By the end of the workshop, you will have a functional, fully automated CI/CD pipeline in your repository, with a clear understanding of how to extend and optimize it.

Whether you are a beginner or have some experience, we will set up GitLab CI from scratch in a repository without CI step by step using an example. Depending on the previous knowledge of the participants, this can be an application with a backend in Java or Python and a frontend using React or Angular with TypeScript. The knowledge conveyed in this workshop can be applied to any technology, though. The focus is on showing best practices so that every person can understand every part of it and leave no questions unanswered.

Apart from basic concepts like branch management, stages, jobs and dependencies between jobs, artifacts, parallelization, and environment variables, the following aspects will be automated:
<ul>
<li>Build, test and deployment</li>
<li>Enforcing good development practices (for example static code analysis, code formatting, secret scanning, style guides and commit guidelines)</li>
<li>Release process including automated version bumps using semantic versioning and changelog generation</li>
<li>Update of dependencies</li>
</ul>
Ideally, the workshop will be interactive, where the focus will be tailored to the needs and interests of the participants.`,
  },
  sicherheitOSS: {
    title: "Sicherheit in Open Source",
    type: SessionType.Talk,
    languageCode: "de",
    durationMinutes: [30, 45],
    description: `Auf der einen Seite gibt es zum Beispiel Android und Linux, die ihren Source Code öffentlich stellen und auf der anderen Seite gibt es Firmen, die um jeden Preis versuchen zu verhindern, dass ihr Source Code an die Öffentlichkeit gelangt. Alleine im letzten Jahr gelang es nicht einmal grossen Unternehmen wie Microsoft, Samsung, Nvidia und Twitch zu verhindern, dass ihr Source Code von Hackern veröffentlicht wurde. Für viele klingt das nach einem Alptraum, doch was für Konsequenzen hat das wirklich?

In diesem Talk geht es darum aufzuzeigen ob und warum Open Source sicherer ist und was es für Sicherheitsrisiken oder auch Vorteile sowohl bei der Verwendung, als auch bei der Bereitstellung von Open Source gibt.`,
  },
  mobileTestingCrossPlatform: {
    title:
      "1 Codebase, 2 Mobile Platforms: How to Test iOS and Android by Just Writing Tests Once",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: [30, 45],
    description: `Developing consistent mobile apps on iOS and Android is difficult enough, but writing automated end to end tests so they run on both platforms without writing them twice is even harder. The limited selectors on mobile make it very difficult to only use one testing code base for both platforms. By using accessibilityIds with translation keys in the same way as in the app, we use the fastest and least brittle selector on mobile possible, being resistant to tests breaking in case of text changes, allowing to also check the app in all languages without any additional effort.

Using a hybrid framework like React Native or NativeScript with WebdriverIO, François will show how to use utility methods to cleverly encapsulate all of the platform differences as much as possible, so writing tests is efficient, readable and maintainable.

Key Takeaways:
<ul>
<li>Learn how to apply approaches from developing multi-platform apps with just one codebase (as used in NativeScript, React Native and Flutter) to end-to-end test automation</li>
<li>Get to know how to easily write tests in a way you can have them run on multiple platforms (even at the same time!) by just writing tests once</li>
<li>Find out which selectors to use for testing multi-platform apps with the least effort possible</li>
<li>Learn about testing accessibility and internationalization without any additional effort</li>
<li>Increase maintainability and readability of your automated end-to-end tests</li>
</ul>`,
  },
  jfxDaysKeynote: {
    title:
      "Presenting PreferencesFX and WorkbenchFX during the opening keynote",
    type: SessionType.Talk,
    languageCode: "en",
    durationMinutes: 15,
  },
};

export const LOCATIONS = {
  amsterdam: {
    city: "Amsterdam",
    country: "Netherlands",
    coordinates: [4.9041, 52.3676],
  },
  athens: {
    city: "Athens",
    country: "Greece",
    coordinates: [23.7275, 37.9838],
  },
  atlanta: {
    city: "Atlanta, GA",
    country: "USA",
    coordinates: [-84.388, 33.749],
  },
  basel: {
    city: "Basel",
    country: "Switzerland",
    coordinates: [7.5886, 47.5596],
  },
  barcelona: {
    city: "Barcelona",
    country: "Spain",
    coordinates: [2.1734, 41.3851],
  },
  berlin: {
    city: "Berlin",
    country: "Germany",
    coordinates: [13.405, 52.52],
  },
  casablanca: {
    city: "Casablanca",
    country: "Morocco",
    coordinates: [-7.62, 33.5945],
  },
  chicago: {
    city: "Chicago, IL",
    country: "USA",
    coordinates: [-87.6298, 41.8781],
  },
  coimbra: {
    city: "Coimbra",
    country: "Portugal",
    coordinates: [-8.4292, 40.2033],
  },
  cologne: {
    city: "Cologne",
    country: "Germany",
    coordinates: [6.9603, 50.9375],
  },
  copenhagen: {
    city: "Copenhagen",
    country: "Denmark",
    coordinates: [12.5683, 55.6761],
  },
  ede: {
    city: "Ede",
    country: "Netherlands",
    coordinates: [5.6585, 52.044],
  },
  jacksonville: {
    city: "Jacksonville, FL",
    country: "USA",
    coordinates: [-81.6557, 30.3322],
  },
  stockholm: {
    city: "Stockholm",
    country: "Sweden",
    coordinates: [18.0686, 59.3293],
  },
  kansasCity: {
    city: "Kansas City, MO",
    country: "USA",
    coordinates: [-94.5786, 39.0997],
  },
  krakow: {
    city: "Kraków",
    country: "Poland",
    coordinates: [19.9842, 50.0886],
  },
  lillestrom: {
    city: "Lillestrøm",
    country: "Norway",
    coordinates: [11.0412, 59.9552],
  },
  london: {
    city: "London",
    country: "United Kingdom",
    coordinates: [-0.1276, 51.5072],
  },
  luxembourg: {
    city: "Luxembourg",
    country: "Luxembourg",
    coordinates: [6.1296, 49.6116],
  },
  lucerne: {
    city: "Lucerne",
    country: "Switzerland",
    coordinates: [8.3059, 47.0502],
  },
  lugano: {
    city: "Lugano",
    country: "Switzerland",
    coordinates: [8.9511, 46.0037],
  },
  madison: {
    city: "Madison, NJ",
    country: "USA",
    coordinates: [-74.4171, 40.7598],
  },
  miami: {
    city: "Miami, FL",
    country: "USA",
    coordinates: [-80.1918, 25.7617],
  },
  oslo: {
    city: "Oslo",
    country: "Norway",
    coordinates: [10.7522, 59.9139],
  },
  rust: {
    city: "Rust",
    country: "Germany",
    coordinates: [7.722, 48.266],
  },
  sanFrancisco: {
    city: "San Francisco, CA",
    country: "USA",
    coordinates: [-122.4194, 37.7749],
  },
  sofia: {
    city: "Sofia",
    country: "Bulgaria",
    coordinates: [23.3219, 42.6977],
  },
  sopot: {
    city: "Sopot",
    country: "Poland",
    coordinates: [18.5601, 54.4416],
  },
  stGallen: {
    city: "St. Gallen",
    country: "Switzerland",
    coordinates: [9.3767, 47.4245],
  },
  tampa: {
    city: "Tampa, FL",
    country: "USA",
    coordinates: [-82.4572, 27.9506],
  },
  tokyo: {
    city: "Tokyo",
    country: "Japan",
    coordinates: [139.6917, 35.6895],
  },
  vienna: {
    city: "Vienna",
    country: "Austria",
    coordinates: [16.3738, 48.2082],
  },
  vilnius: {
    city: "Vilnius",
    country: "Lithuania",
    coordinates: [25.2797, 54.6872],
  },
  zurich: {
    city: "Zürich",
    country: "Switzerland",
    coordinates: [8.5417, 47.3769],
  },
} satisfies Record<string, LocationDefinition>;

export const SESSIONS_ARRAY: Session[] = Object.values(SESSIONS);
export const EVENTS: EventItem[] = [
  {
    name: "Tokyo Test Fest\u00A02026",
    startDate: "2026-11-12",
    endDate: "2026-11-13",
    location: LOCATIONS.tokyo,
    website: "https://tokyotestfest.com/en/",
    talks: [SESSIONS.testingToolbox],
  },
  {
    name: "Devoxx Morocco\u00A02026",
    startDate: "2026-11-04",
    endDate: "2026-11-05",
    location: LOCATIONS.casablanca,
    website: "https://devoxx.ma",
    talks: [SESSIONS.teachingAiBetterCodingHabits],
  },
  {
    name: "TestCon Europe\u00A02026",
    startDate: "2026-10-21",
    endDate: "2026-10-23",
    location: LOCATIONS.vilnius,
    website: "https://testcon.lt",
    talks: [
      {
        ...SESSIONS.flakyTests,
        durationMinutes: 45,
      },
      {
        ...SESSIONS.workshopContributingOSS,
        durationMinutes: 360,
      },
    ],
  },
  {
    name: "BaselOne\u00A02026",
    startDate: "2026-10-14",
    endDate: "2026-10-15",
    location: LOCATIONS.basel,
    website: "https://baselone.ch",
    talks: [
      {
        ...SESSIONS.bugs82,
        durationMinutes: 45,
      },
    ],
  },
  {
    name: "BED-Con\u00A02026",
    startDate: "2026-09-23",
    endDate: "2026-09-24",
    location: LOCATIONS.berlin,
    website: "https://bed-con.org/",
    talks: [SESSIONS.bugs82],
  },
  {
    name: "KCDC\u00A02026",
    startDate: "2026-09-09",
    endDate: "2026-09-12",
    location: LOCATIONS.kansasCity,
    website: "https://www.kcdc.info/",
    talks: [
      SESSIONS.jmh,
      SESSIONS.testingToolbox,
      {
        ...SESSIONS.mentoringNextSteps,
        durationMinutes: 60,
      },
      {
        ...SESSIONS.mentoringConferences,
        durationMinutes: 60,
      },
    ],
  },
  {
    name: "Garden State Java User Group\u00A02026",
    startDate: "2026-09-02",
    endDate: "2026-09-02",
    location: LOCATIONS.madison,
    website: "https://gsjug.org/meetings/2026/sep2026a.html",
    talks: [SESSIONS.testingToolbox],
  },
  {
    name: "Voxxed Days Luxembourg\u00A02026",
    startDate: "2026-06-18",
    endDate: "2026-06-19",
    location: LOCATIONS.luxembourg,
    website: "https://luxembourg.voxxeddays.com/en/",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=d_yv_pn3GkQ",
        slidesUrl:
          "https://github.com/martinfrancois/voxxed-days-luxembourg-2026/blob/main/Voxxed_Days_Luxembourg-2026-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
      {
        ...SESSIONS.testingToolbox,
        recordingUrl: "https://www.youtube.com/watch?v=AS2j_I7P16A",
      },
    ],
  },
  {
    name: "DevBcn\u00A02026",
    startDate: "2026-06-16",
    endDate: "2026-06-17",
    location: LOCATIONS.barcelona,
    website: "https://www.devbcn.com/2026/talks/1173255?q=Fran%C3%A7ois",
    talks: [
      {
        ...SESSIONS.askMeAnything,
        slotLabel: "Tue, 16 Jun slot",
      },
      SESSIONS.publicSpeakingMentoring,
      {
        ...SESSIONS.askMeAnything,
        slotLabel: "Wed, 17 Jun slot",
      },
      {
        ...SESSIONS.treeShaking,
        durationMinutes: 20,
        slidesUrl:
          "https://github.com/martinfrancois/devbcn-2026/blob/main/DevBcn-2026-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "Java User Group CH Basel\u00A02026",
    startDate: "2026-06-09",
    endDate: "2026-06-09",
    location: LOCATIONS.basel,
    website: "https://www.jug.ch/html/events/2026/java-streams_bs.html",
    talks: [
      {
        ...SESSIONS.javaStreams,
        slidesUrl:
          "https://github.com/martinfrancois/jug-basel-2026/blob/main/JUG_Basel-2026-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/jug-basel-2026/blob/main/code.md",
      },
    ],
  },
  {
    name: "jPrime\u00A02026",
    startDate: "2026-06-03",
    endDate: "2026-06-04",
    location: LOCATIONS.sofia,
    website: "https://jprime.io",
    talks: [
      {
        ...SESSIONS.jmh,
        recordingUrl: "https://www.youtube.com/watch?v=ZnUyXOl6okA",
        slidesUrl:
          "https://github.com/martinfrancois/jprime-2026/blob/main/jPrime-2026-My_code_is_faster_than_yours_let_me_prove_it_to_you.pdf",
      },
    ],
  },
  {
    name: "GeeCON\u00A02026",
    startDate: "2026-05-14",
    endDate: "2026-05-15",
    location: LOCATIONS.krakow,
    website: "https://2026.geecon.org/",
    talks: [
      {
        ...SESSIONS.bugs82,
        durationMinutes: 50,
        recordingUrl: "https://www.youtube.com/watch?v=bAq6OyaPKYg",
        slidesUrl:
          "https://github.com/martinfrancois/geecon-2026/blob/main/GeeCON-2026-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
      {
        ...SESSIONS.aiAssistedCodingPanel,
        recordingUrl: "https://www.youtube.com/watch?v=DeUJaScxe3I",
      },
    ],
  },
  {
    name: "JCON EUROPE\u00A02026",
    startDate: "2026-04-20",
    endDate: "2026-04-23",
    location: LOCATIONS.cologne,
    website: "https://2026.europe.jcon.one/",
    talks: [SESSIONS.mentoringConferences, SESSIONS.mentoringNextSteps],
  },
  {
    name: "JavaLand\u00A02026",
    startDate: "2026-03-10",
    endDate: "2026-03-12",
    location: LOCATIONS.rust,
    website: "https://meine.doag.org/events/javaland/2026/agenda/#eventDay.all",
    talks: [
      {
        ...SESSIONS.bugs82,
        durationMinutes: 40,
        slidesUrl:
          "https://github.com/martinfrancois/javaland-2026/blob/main/JavaLand-2026-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "Jfokus\u00A02026",
    startDate: "2026-02-02",
    endDate: "2026-02-04",
    location: LOCATIONS.stockholm,
    website: "https://www.jfokus.se/talks.html",
    talks: [
      {
        ...SESSIONS.javaStreams,
        durationMinutes: 90,
        slidesUrl:
          "https://github.com/martinfrancois/jfokus-2026/blob/main/Jfokus-2026-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/jfokus-2026/blob/main/code.md",
      },
      SESSIONS.mentoringConferences,
      SESSIONS.mentoringNextSteps,
    ],
  },
  {
    name: "Java User Group CH St. Gallen\u00A02026",
    startDate: "2026-01-15",
    endDate: "2026-01-15",
    location: LOCATIONS.stGallen,
    website: "https://www.jug.ch/html/events/2026/java-streams_sg.html",
    talks: [
      {
        ...SESSIONS.javaStreams,
        durationMinutes: 75,
        slidesUrl:
          "https://github.com/martinfrancois/jug-st-gallen-2026/blob/main/JUG_St_Gallen-2026-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/jug-st-gallen-2026/blob/main/code.md",
      },
    ],
  },
  {
    name: "Java User Group CH Lucerne\u00A02025",
    startDate: "2025-11-20",
    endDate: "2025-11-20",
    location: LOCATIONS.lucerne,
    website: "https://www.jug.ch/html/events/2025/java-streams_lu.html",
    talks: [
      {
        ...SESSIONS.javaStreams,
        durationMinutes: 75,
        slidesUrl:
          "https://github.com/martinfrancois/jug-lucerne-2025/blob/main/JUG-Lucerne-2025-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/jug-lucerne-2025/blob/main/code.md",
      },
    ],
  },
  {
    name: "GOhack25",
    startDate: "2025-11-06",
    endDate: "2025-11-08",
    location: LOCATIONS.zurich,
    website: "https://gobugfree.com/gohack",
    talks: [
      {
        ...SESSIONS.vulnDisclosure,
        slidesUrl:
          "https://github.com/martinfrancois/gohack25/blob/main/GOHack25-The_Art_of_Vulnerability_Disclosure.pdf",
      },
    ],
  },
  {
    name: "J-Fall\u00A02025",
    startDate: "2025-11-06",
    endDate: "2025-11-06",
    location: LOCATIONS.ede,
    website: "https://jfall.nl/",
    talks: [
      {
        ...SESSIONS.jmh,
        recordingUrl: "https://www.youtube.com/watch?v=lKxxMCJnZ9A",
        slidesUrl:
          "https://github.com/martinfrancois/jfall-2025/blob/main/JFall-2025-My_code_is_faster_than_yours_let_me_prove_it_to_you.pdf",
      },
      SESSIONS.mentoringNextSteps,
    ],
  },
  {
    name: "Amsterdam JUG\u00A02025",
    startDate: "2025-11-04",
    endDate: "2025-11-04",
    location: LOCATIONS.amsterdam,
    website:
      "https://www.meetup.com/amsterdam-java-user-group/events/311701262/",
    talks: [
      {
        ...SESSIONS.jmh,
        slidesUrl:
          "https://github.com/martinfrancois/jug-amsterdam-2025/blob/main/JUG_Amsterdam-2025-My_code_is_faster_than_yours_let_me_prove_it_to_you.pdf",
      },
    ],
  },
  {
    name: "TestCon Europe\u00A02025",
    startDate: "2025-10-22",
    endDate: "2025-10-24",
    location: LOCATIONS.vilnius,
    website: "https://testcon.lt",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=4HM-AVrjxn8",
        slidesUrl:
          "https://github.com/martinfrancois/testcon-europe-2025/blob/main/TestCon_Europe-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "BaselOne\u00A02025",
    startDate: "2025-10-15",
    endDate: "2025-10-16",
    location: LOCATIONS.basel,
    website: "https://baselone.ch",
    talks: [
      {
        ...SESSIONS.javaStreams,
        slidesUrl:
          "https://github.com/martinfrancois/baselone-2025/blob/main/BaselOne-2025-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/baselone-2025/blob/main/code.md",
      },
    ],
  },
  {
    name: "San Francisco Java User Group\u00A02025",
    startDate: "2025-09-25",
    endDate: "2025-09-25",
    location: LOCATIONS.sanFrancisco,
    website: "https://www.meetup.com/sfjava/events/311009783/",
    talks: [
      {
        ...SESSIONS.flakyTests,
        slidesUrl:
          "https://blog.bdemers.io/presentations/2025/sfjug-testing-on-thin-ice/",
      },
      SESSIONS.testingToolbox,
    ],
  },
  {
    name: "DPE Summit\u00A02025",
    startDate: "2025-09-23",
    endDate: "2025-09-24",
    location: LOCATIONS.sanFrancisco,
    website: "https://dpesummit.com/",
    talks: [
      {
        ...SESSIONS.flakyTests,
        slidesUrl:
          "https://blog.bdemers.io/presentations/2025/dpe-summit-testing-on-thin-ice/",
        recordingUrl: "https://www.youtube.com/watch?v=kwxwHuScmLk",
      },
    ],
  },
  {
    name: "Java User Group CH Zürich\u00A02025",
    startDate: "2025-09-16",
    endDate: "2025-09-16",
    location: LOCATIONS.zurich,
    website: "https://www.jug.ch/html/events/2025/java-streams.html",
    talks: [
      {
        ...SESSIONS.javaStreams,
        durationMinutes: 75,
        slidesUrl:
          "https://github.com/martinfrancois/jug-zurich-2025/blob/main/JUG-Zurich-2025-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/jug-zurich-2025/blob/main/code.md",
      },
    ],
  },
  {
    name: "CH Open Workshop-Tage\u00A02025",
    startDate: "2025-09-09",
    endDate: "2025-09-11",
    location: LOCATIONS.zurich,
    website: "https://workshoptage.ch/",
    talks: [SESSIONS.workshopContributingOSS],
  },
  {
    name: "JavaZone\u00A02025",
    startDate: "2025-09-03",
    endDate: "2025-09-04",
    location: LOCATIONS.lillestrom,
    website: "https://2025.javazone.no/en",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://vimeo.com/1115907704",
        slidesUrl:
          "https://github.com/martinfrancois/javazone-2025/blob/main/JavaZone-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "DiWoDo\u00A02025",
    startDate: "2025-09-01",
    endDate: "2025-09-06",
    website: "https://www.diwodo.de/",
    talks: [
      {
        ...SESSIONS.browser2025,
        recordingUrl: "https://www.youtube.com/watch?v=DVe7uZiQo0w",
        slidesUrl:
          "https://github.com/martinfrancois/diwodo-2025/blob/main/DiWoDo-2025-Browser.pdf",
      },
    ],
  },
  {
    name: "Chicago Java Users Group\u00A02025",
    startDate: "2025-08-19",
    endDate: "2025-08-19",
    location: LOCATIONS.chicago,
    website: "https://lu.ma/2ii512i5",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=KtFS-K_dffU",
        slidesUrl:
          "https://github.com/martinfrancois/cjug-2025/blob/main/CJUG-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "KCDC\u00A02025",
    startDate: "2025-08-13",
    endDate: "2025-08-15",
    location: LOCATIONS.kansasCity,
    website: "https://www.kcdc.info",
    talks: [
      {
        ...SESSIONS.bugs82,
        slidesUrl:
          "https://github.com/martinfrancois/kcdc-2025/blob/main/KCDC-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
      {
        ...SESSIONS.treeShaking,
        slidesUrl:
          "https://github.com/martinfrancois/kcdc-2025/blob/main/KCDC-2025-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "WeAreDevelopers World Congress\u00A02025",
    startDate: "2025-07-09",
    endDate: "2025-07-11",
    location: LOCATIONS.berlin,
    website: "https://worldcongress.dev",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl:
          "https://www.wearedevelopers.com/en/videos/1581/82-bugs-i-collected-in-a-year-you-won-t-believe-made-it-to-production",
        slidesUrl:
          "https://github.com/martinfrancois/wearedevelopers-2025/blob/main/WeAreDevelopers_2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "JNation\u00A02025",
    startDate: "2025-05-27",
    endDate: "2025-05-28",
    location: LOCATIONS.coimbra,
    website: "https://jnation.pt",
    talks: [
      {
        ...SESSIONS.jmh,
        recordingUrl: "https://www.youtube.com/watch?v=qh4CXTHx880",
        slidesUrl:
          "https://github.com/martinfrancois/jnation-2025/blob/main/JNation-2025-My_code_is_faster_than_yours_let_me_prove_it_to_you.pdf",
      },
    ],
  },
  {
    name: "NDC Oslo\u00A02025",
    startDate: "2025-05-19",
    endDate: "2025-05-23",
    location: LOCATIONS.oslo,
    website: "https://ndcoslo.com",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=fs8oimP2REQ",
        slidesUrl:
          "https://github.com/martinfrancois/ndc-oslo-2025/blob/main/NDC_Oslo-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
      {
        ...SESSIONS.treeShaking,
        slidesUrl:
          "https://github.com/martinfrancois/ndc-oslo-2025/blob/main/NDC_Oslo-2025-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "JCON EUROPE\u00A02025",
    startDate: "2025-05-12",
    endDate: "2025-05-15",
    location: LOCATIONS.cologne,
    website: "https://jcon.koeln",
    talks: [
      {
        ...SESSIONS.testingToolbox,
        recordingUrl: "https://www.youtube.com/watch?v=-f0YRQlIiNM",
      },
      SESSIONS.mentoringConferences,
      SESSIONS.mentoringNextSteps,
    ],
  },
  {
    name: "Devoxx Greece\u00A02025",
    startDate: "2025-04-10",
    endDate: "2025-04-12",
    location: LOCATIONS.athens,
    website: "https://devoxx.gr/",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=x4C4YQKKWuM",
        slidesUrl:
          "https://github.com/martinfrancois/devoxx-greece-2025/blob/main/Devoxx_Greece-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "Voxxed Days Zürich\u00A02025",
    startDate: "2025-03-25",
    endDate: "2025-03-25",
    location: LOCATIONS.zurich,
    website: "https://zurich.voxxeddays.com/",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=HS1JV_y5HlE",
        slidesUrl:
          "https://github.com/martinfrancois/voxxed-days-zurich-2025/blob/main/Voxxed_Days_Zurich-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "Tampa Java User Group\u00A02025",
    startDate: "2025-03-13",
    endDate: "2025-03-13",
    location: LOCATIONS.tampa,
    website: "https://www.meetup.com/tampa-jug/events/306456169/",
    talks: [
      {
        ...SESSIONS.testingWaste,
        slidesUrl:
          "https://github.com/martinfrancois/tampa-jug-2025/blob/main/TJUG-2025-Why_Software_Testing_Is_a_Waste_of_Time.pdf",
      },
    ],
  },
  {
    name: "Jacksonville Java User Group (JaxJUG)\u00A02025",
    startDate: "2025-03-12",
    endDate: "2025-03-12",
    location: LOCATIONS.jacksonville,
    website:
      "https://www.meetup.com/jacksonville-java-user-group-jaxjug/events/306457539/",
    talks: [
      {
        ...SESSIONS.apollo,
        slidesUrl:
          "https://github.com/martinfrancois/jaxjug-2025/blob/main/JaxJUG-2025-One_Small_Test_For_Man.pdf",
      },
    ],
  },
  {
    name: "Miami Java User Group (MJUG)\u00A02025",
    startDate: "2025-03-11",
    endDate: "2025-03-11",
    location: LOCATIONS.miami,
    website: "https://www.meetup.com/miami-java-user-group/events/306473604/",
    talks: [SESSIONS.testingToolbox],
  },
  {
    name: "Devnexus\u00A02025",
    startDate: "2025-03-04",
    endDate: "2025-03-06",
    location: LOCATIONS.atlanta,
    website: "https://devnexus.com/",
    talks: [
      {
        ...SESSIONS.flakyTests,
        slidesUrl:
          "https://blog.bdemers.io/presentations/2025/devnexus-testing-on-thin-ice/",
      },
      SESSIONS.mentoringConferences,
      SESSIONS.mentoringNextSteps,
    ],
  },
  {
    name: "Devworld Conference\u00A02025",
    startDate: "2025-02-27",
    endDate: "2025-02-28",
    location: LOCATIONS.amsterdam,
    website: "https://devworldconference.com",
    talks: [
      {
        ...SESSIONS.javaStreams,
        slidesUrl:
          "https://github.com/martinfrancois/devworld-2025/blob/main/DEVWorld-2025-I_Did_not_Know_You_Could_Do_That_With_Java_Streams.pdf",
        codeUrl:
          "https://github.com/martinfrancois/devworld-2025/blob/main/code.md",
        recordingUrl: "https://www.youtube.com/watch?v=jBiErfryCH8",
      },
    ],
  },
  {
    name: "NDC London\u00A02025",
    startDate: "2025-01-27",
    endDate: "2025-01-31",
    location: LOCATIONS.london,
    website: "https://ndclondon.com/",
    talks: [
      {
        ...SESSIONS.bugs82,
        recordingUrl: "https://www.youtube.com/watch?v=QeGKr8n-CLY",
        slidesUrl:
          "https://github.com/martinfrancois/ndc-london-2025/blob/main/NDC_London-2025-82_Bugs_I_Collected_in_a_Year_You_Wont_Believe_Made_It_to_Production.pdf",
      },
    ],
  },
  {
    name: "Voxxed Days Ticino\u00A02025",
    startDate: "2025-01-17",
    endDate: "2025-01-17",
    location: LOCATIONS.lugano,
    website: "https://ticino.voxxeddays.com/",
    talks: [
      {
        ...SESSIONS.treeShaking,
        recordingUrl: "https://www.youtube.com/watch?v=t2V1ZzWh8k0",
        slidesUrl:
          "https://github.com/martinfrancois/voxxed-days-ticino-2025/blob/main/Voxxed_Days_Ticino-2025-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "TestWarez\u00A02024",
    startDate: "2024-11-06",
    endDate: "2024-11-08",
    location: LOCATIONS.sopot,
    website:
      "https://web.archive.org/web/20250209213242/https://2024.testwarez.pl/en/home_en/",
    talks: [SESSIONS.integrationTestingAndDevelopment],
  },
  {
    name: "TestCon Europe\u00A02024",
    startDate: "2024-10-23",
    endDate: "2024-10-25",
    location: LOCATIONS.vilnius,
    website: "https://testcon.lt",
    talks: [
      {
        ...SESSIONS.testingToolbox,
        recordingUrl: "https://www.youtube.com/watch?v=frVv3P4oQFM",
      },
      {
        ...SESSIONS.panelAutomation,
        recordingUrl: "https://www.youtube.com/watch?v=wm9g-ZZ_JZY",
      },
      {
        ...SESSIONS.apollo,
        recordingUrl: "https://www.youtube.com/watch?v=BkETjwnGAq8",
        slidesUrl:
          "https://github.com/martinfrancois/testcon-europe-2024/blob/main/TestCon_Europe-2024-One_Small_Test_For_Man.pdf",
      },
    ],
  },
  {
    name: "BaselOne\u00A02024",
    startDate: "2024-10-16",
    endDate: "2024-10-17",
    location: LOCATIONS.basel,
    website: "https://baselone.ch",
    talks: [
      {
        ...SESSIONS.jmh,
        recordingUrl: "https://www.youtube.com/watch?v=bF0DKyM77_4",
        slidesUrl:
          "https://github.com/martinfrancois/baselone-2024/blob/main/BaselOne-2024-My_code_is_faster_than_yours_let_me_prove_it_to_you.pdf",
      },
    ],
  },
  {
    name: "DiWoDo\u00A02024",
    startDate: "2024-09-23",
    endDate: "2024-09-28",
    website: "https://www.diwodo.de/",
    talks: [
      {
        ...SESSIONS.browser2024,
        recordingUrl: "https://www.youtube.com/watch?v=dgNtt6HtL7g",
        slidesUrl:
          "https://github.com/martinfrancois/diwodo-2024/blob/main/DiWoDo-2024-Browser.pdf",
      },
    ],
  },
  {
    name: "CH Open Workshop-Tage\u00A02024",
    startDate: "2024-09-02",
    endDate: "2024-09-05",
    location: LOCATIONS.lucerne,
    website: "https://workshoptage.ch/",
    talks: [SESSIONS.workshopGitlabCI],
  },
  {
    name: "Hands On Testing\u00A02024",
    startDate: "2024-08-27",
    endDate: "2024-08-31",
    website: "https://handsontesting.com/",
    talks: [
      {
        ...SESSIONS.apollo,
        recordingUrl: "https://www.youtube.com/watch?v=MCkrETQ7Esc",
        slidesUrl:
          "https://github.com/martinfrancois/hands-on-testing-2024/blob/main/Hands_on_Testing-2024-One_Small_Test_For_Man.pdf",
      },
    ],
  },
  {
    name: "Copenhagen Developers Festival\u00A02024",
    startDate: "2024-08-26",
    endDate: "2024-08-30",
    location: LOCATIONS.copenhagen,
    website: "https://cphdevfest.com",
    talks: [
      {
        ...SESSIONS.treeShaking,
        recordingUrl: "https://www.youtube.com/watch?v=WT-mIjeTzqc",
        slidesUrl:
          "https://github.com/martinfrancois/copenhagen-developers-festival-2024/blob/main/Copenhagen_Developers_Festival-2024-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "WeAreDevelopers World Congress\u00A02024",
    startDate: "2024-07-17",
    endDate: "2024-07-19",
    location: LOCATIONS.berlin,
    website: "https://worldcongress.dev",
    talks: [
      {
        ...SESSIONS.treeShaking,
        slidesUrl:
          "https://github.com/martinfrancois/wearedevelopers-2024/blob/main/WeAreDevelopers_2024-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "DeveloperWeek Europe\u00A02024",
    startDate: "2024-06-05",
    endDate: "2024-06-06",
    website: "https://www.developerweek.com/europe/conference/",
    talks: [
      {
        ...SESSIONS.treeShaking,
        slidesUrl:
          "https://github.com/martinfrancois/developerweek-europe-2024/blob/main/Developer_Week_Europe_2024-How_writing_just_one_import_the_wrong_way_slows_down_your_website.pdf",
      },
    ],
  },
  {
    name: "JCON EUROPE\u00A02024",
    startDate: "2024-05-13",
    endDate: "2024-05-16",
    location: LOCATIONS.cologne,
    website: "https://europe.jcon.one",
    talks: [
      {
        ...SESSIONS.flakyTests,
        recordingUrl: "https://www.youtube.com/watch?v=vJyY7x69p0Y",
        slidesUrl:
          "https://blog.bdemers.io/presentations/2024/jcon-testing-on-thin-ice/",
      },
    ],
  },
  {
    name: "Software Quality Days\u00A02024",
    startDate: "2024-04-23",
    endDate: "2024-04-25",
    location: LOCATIONS.vienna,
    website:
      "https://web.archive.org/web/20240224010023/https://www.software-quality-days.com/en/conference-program?tx_comot_pi2%5Baction%5D=index&tx_comot_pi2%5Bcontroller%5D=Planner&tx_comot_pi2%5Bday%5D=85",
    talks: [
      {
        ...SESSIONS.testingWaste,
        slidesUrl:
          "https://github.com/martinfrancois/software-quality-days-2024/blob/main/Software_Quality_Days_2024-Why_Software_Testing_Is_a_Waste_of_Time.pdf",
      },
    ],
  },
  {
    name: "Devnexus\u00A02024",
    startDate: "2024-04-09",
    endDate: "2024-04-11",
    location: LOCATIONS.atlanta,
    website: "https://devnexus.com/",
    talks: [
      {
        ...SESSIONS.vulnDisclosure,
        slidesUrl:
          "https://github.com/martinfrancois/devnexus-2024/blob/main/Devnexus_2024-The_Art_of_Vulnerability_Disclosure.pdf",
      },
    ],
  },
  {
    name: "JCON WORLD\u00A02023",
    startDate: "2023-11-21",
    endDate: "2023-11-23",
    website: "https://world.jcon.one",
    talks: [
      {
        ...SESSIONS.browserSnitch,
        recordingUrl: "https://www.youtube.com/watch?v=YmV1rsc9L9E",
        slidesUrl:
          "https://github.com/martinfrancois/jcon-world-2023/blob/main/JCON_World-2023-How_Your_Browser_Is_Snitching_on_You.pdf",
      },
    ],
  },
  {
    name: "DiWoDo\u00A02023",
    startDate: "2023-09-25",
    endDate: "2023-09-30",
    website: "https://diwodo.de",
    talks: [
      {
        ...SESSIONS.browser2023,
        recordingUrl: "https://www.youtube.com/watch?v=yCf7jr3ytPY",
        slidesUrl:
          "https://github.com/martinfrancois/diwodo-2023/blob/main/DiWoDo23_Browser.pdf",
      },
    ],
  },
  {
    name: "A4Q Global Testing Summit\u00A02023",
    startDate: "2023-09-14",
    endDate: "2023-09-14",
    location: LOCATIONS.zurich,
    website:
      "https://web.archive.org/web/20230908232127/https://a4qworldcongress.com/program/",
    talks: [
      {
        ...SESSIONS.testingWaste,
        recordingUrl: "https://www.youtube.com/watch?v=-McUTz5txb0",
        slidesUrl:
          "https://github.com/martinfrancois/a4qtestingsummit-2023/blob/main/A4QTestingSummit-2023-Why_Software_Testing_Is_a_Waste_of_Time.pdf",
      },
    ],
  },
  {
    name: "JCON EUROPE\u00A02023",
    startDate: "2023-06-20",
    endDate: "2023-06-23",
    location: LOCATIONS.cologne,
    website: "https://europe.jcon.one",
    talks: [
      {
        ...SESSIONS.testingWaste,
        recordingUrl: "https://www.youtube.com/watch?v=qQVhb3z244Q",
        slidesUrl:
          "https://github.com/martinfrancois/jcon-europe-2023/blob/main/JCON_EUROPE-2023-Why_Software_Testing_Is_a_Waste_of_Time.pdf",
      },
    ],
  },
  {
    name: "DiWoDo\u00A02022",
    startDate: "2022-09-26",
    endDate: "2022-09-30",
    website: "https://diwodo.de",
    talks: [
      {
        ...SESSIONS.browser,
        recordingUrl: "https://www.youtube.com/watch?v=X0VY2w_5Ao8",
        slidesUrl:
          "https://github.com/martinfrancois/diwodo-2022/blob/main/DiWoDo22_Browser.pdf",
      },
      {
        ...SESSIONS.sicherheitOSS,
        recordingUrl: "https://www.youtube.com/watch?v=awoesecxGFo",
        slidesUrl:
          "https://github.com/martinfrancois/diwodo-2022/blob/main/DiWoDo22_Sicherheit_OSS.pdf",
      },
    ],
  },
  {
    name: "SauceCon Online\u00A02020",
    startDate: "2020-05-12",
    endDate: "2020-05-14",
    website: "https://web.archive.org/web/20200511210606/https://saucecon.com/",
    talks: [
      {
        ...SESSIONS.mobileTestingCrossPlatform,
        recordingUrl: "https://www.youtube.com/watch?v=ElfbKHXRjKs",
        slidesUrl:
          "https://github.com/martinfrancois/saucecon-2020-1-codebase-2-mobile-platforms/blob/main/SauceCon_2020_Online.pdf",
        codeUrl: "https://github.com/martinfrancois/wdio-mobile-utils-demo",
      },
    ],
  },
  {
    name: "JavaFX Days\u00A02018",
    startDate: "2018-12-03",
    endDate: "2018-12-05",
    location: LOCATIONS.zurich,
    website:
      "https://web.archive.org/web/20181011044741/https://dlsc.com/2018/08/27/javafx-days-zurich-sessions/",
    talks: [
      {
        ...SESSIONS.jfxDaysKeynote,
        recordingUrl: "https://www.youtube.com/watch?v=Lg6Hvj7_P14&t=380",
      },
    ],
  },
];
